import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { products } from '../data/mockData';
import '../styles/Products.css';

const Products: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [filteredProducts, setFilteredProducts] = useState(products);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [displayedProducts, setDisplayedProducts] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const PRODUCTS_PER_PAGE = 20;

  const category = searchParams.get('category');
  const subcategory = searchParams.get('subcategory');
  const search = searchParams.get('search');

  useEffect(() => {
    let filtered = [...products];

    if (search) {
      filtered = filtered.filter((p) =>
        p.description.toLowerCase().includes(search.toLowerCase()) ||
        p.id.toLowerCase().includes(search.toLowerCase())
      );
    } else if (category) {
      filtered = filtered.filter((p) => p.bigClass === category);
      if (subcategory) {
        filtered = filtered.filter((p) => p.smallClass === subcategory);
      }
    }

    setFilteredProducts(filtered);
    setPage(1);
    setDisplayedProducts(filtered.slice(0, PRODUCTS_PER_PAGE));
  }, [category, subcategory, search]);

  const loadMoreProducts = React.useCallback(() => {
    if (loading) return;
    if (displayedProducts.length >= filteredProducts.length) return;

    setLoading(true);
    setTimeout(() => {
      const nextPage = page + 1;
      const start = page * PRODUCTS_PER_PAGE;
      const end = start + PRODUCTS_PER_PAGE;
      const newProducts = filteredProducts.slice(start, end);

      setDisplayedProducts(prev => [...prev, ...newProducts]);
      setPage(nextPage);
      setLoading(false);
    }, 300);
  }, [loading, displayedProducts.length, filteredProducts, page, PRODUCTS_PER_PAGE]);

  useEffect(() => {
    const handleScroll = () => {
      if (loading) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 500) {
        loadMoreProducts();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, loadMoreProducts]);

  const handleProductClick = (product: any) => {
    setSelectedProduct(product);
  };

  const handleBackToList = () => {
    setSelectedProduct(null);
  };

  if (selectedProduct) {
    return (
      <div className="product-detail">
        <button className="back-button" onClick={handleBackToList}>
          ← Back to Product List
        </button>
        <div className="product-detail-content">
          <h2>Product ID: {selectedProduct.id}</h2>
          <div className="product-detail-image">
            <img
              src={`/images/products/${selectedProduct.filename}`}
              alt={selectedProduct.id}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const placeholder = document.createElement('div');
                placeholder.className = 'image-placeholder';
                placeholder.innerHTML = `<p>Product Image</p><p class="filename">${selectedProduct.filename}</p>`;
                target.parentNode?.appendChild(placeholder);
              }}
            />
          </div>
          <div className="product-detail-info">
            <p><strong>Category:</strong> {selectedProduct.bigClass}</p>
            <p><strong>Subcategory:</strong> {selectedProduct.smallClass}</p>
            <p><strong>Description:</strong></p>
            <p>{selectedProduct.description}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="products-container">
      <div className="products-header">
        {search ? (
          <>
            <h2>Keyword Search</h2>
            <p>Total search {filteredProducts.length} products</p>
          </>
        ) : category ? (
          <>
            <h2>{category}{subcategory ? ` > ${subcategory}` : ''}</h2>
            <p>This category of products total {filteredProducts.length}</p>
          </>
        ) : (
          <>
            <h2>All Products</h2>
            <p>Browse our complete catalog</p>
          </>
        )}
      </div>

      {filteredProducts.length === 0 ? (
        <div className="no-products">
          <p>More and more products coming soon......</p>
        </div>
      ) : (
        <>
          <div className="products-grid">
            {displayedProducts.map((product) => (
            <div
              key={product.id}
              className="product-card"
              onClick={() => handleProductClick(product)}
            >
              <div className="product-image-container">
                <img
                  src={`/images/products/${product.filename}`}
                  alt={product.id}
                  className="product-image"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = `<div class="product-image-placeholder"><p>Product Image</p><p class="product-filename">${product.filename}</p></div>`;
                    }
                  }}
                />
              </div>
              <div className="product-info">
                <h3>{product.id}</h3>
                <p>{product.description.substring(0, 50)}...</p>
              </div>
            </div>
            ))}
          </div>
          {loading && (
            <div className="loading-indicator">
              <p>Loading more products...</p>
            </div>
          )}
          {displayedProducts.length < filteredProducts.length && !loading && (
            <div className="load-more-info">
              <p>Showing {displayedProducts.length} of {filteredProducts.length} products</p>
              <p>Scroll down to load more</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Products;
